<template>
	<div id="lexi-chat-page" data-aos="fade">
		<!-- Pasos del proceso -->
		<section class="process-bar">
			<div class="container">
				<div class="text-center mb-4">
					<h2 class="process-title">Proceso unlawk</h2>
					<p class="process-subtitle mb-0">Sigue estos 3 pasos para resolver tu problema legal</p>
				</div>

				<div class="row process-steps mx-auto">
					<div class="col-md-4 mb-3 mb-md-0">
						<button
							type="button"
							class="process-step w-100"
							:class="{ active: activeStep === 1 }"
							@click="activeStep = 1"
						>
							<span class="step-num">1</span>
							<span class="step-text text-left">
								<span class="step-name d-block">Consulta con Lexi</span>
								<span class="step-desc d-block">Describe tu problema legal</span>
							</span>
							<span v-if="activeStep === 1" class="step-pulse"></span>
						</button>
					</div>
					<div class="col-md-4 mb-3 mb-md-0">
						<button
							type="button"
							class="process-step process-step-2 w-100"
							:class="{ active: activeStep === 2 }"
							@click="activeStep = 2"
						>
							<span class="step-num">2</span>
							<span class="step-text text-left">
								<span class="step-name d-block">Crea tu Expediente</span>
								<span class="step-desc d-block">Documenta tu caso legal</span>
							</span>
							<span v-if="activeStep === 2" class="step-pulse"></span>
						</button>
					</div>
					<div class="col-md-4">
						<button
							type="button"
							class="process-step process-step-3 w-100"
							:class="{ active: activeStep === 3 }"
							@click="activeStep = 3"
						>
							<span class="step-num">3</span>
							<span class="step-text text-left">
								<span class="step-name d-block">Conecta con Abogados</span>
								<span class="step-desc d-block">Encuentra el especialista ideal</span>
							</span>
							<span v-if="activeStep === 3" class="step-pulse"></span>
						</button>
					</div>
				</div>
			</div>
		</section>

		<section class="content-section">
			<div class="container content-wrap">
				<!-- Paso 1: Chat -->
				<div v-if="activeStep === 1" class="row">
					<div class="col-lg-3 mb-4 mb-lg-0">
						<div class="info-card">
							<h3 class="info-card-title">
								<i class="fa-regular fa-robot mr-2 text-primary"></i>
								Lexi IA
							</h3>
							<p class="info-card-lead">Soy tu asistente legal inteligente. Puedo ayudarte a:</p>
							<ul class="info-list list-unstyled mb-0">
								<li><span class="dot"></span> Entender tu problema legal</li>
								<li><span class="dot"></span> Identificar documentos necesarios</li>
								<li><span class="dot"></span> Recomendar especialistas</li>
								<li><span class="dot"></span> Guiarte en el proceso</li>
							</ul>
							<div class="info-card-note">
								<p class="mb-0 small text-muted">
									<strong>Importante:</strong> No soy un abogado. Esta es orientación inicial, no asesoría legal formal.
								</p>
							</div>
						</div>

						<div v-if="messages.length > 2" class="info-card next-step-card mt-3">
							<h4 class="next-step-title">Siguiente Paso</h4>
							<p class="small text-muted mb-3">¿Listo para formalizar tu caso?</p>
							<b-button variant="success" size="sm" block @click="activeStep = 2">
								<i class="fa-regular fa-robot mr-2"></i>
								Crear Expediente
							</b-button>
						</div>
					</div>

					<div class="col-lg-9">
						<div class="chat-card d-flex flex-column">
							<div class="chat-header">
								<h3 class="chat-title mb-0">
									<i class="fa-regular fa-robot mr-2 text-primary"></i>
									Chat con Lexi
									<span v-if="isTyping" class="typing-label">
										<span class="typing-dot"></span>
										Escribiendo...
									</span>
								</h3>
							</div>

							<div ref="chatScroll" class="chat-body">
								<div v-if="!messages.length" class="chat-empty text-center py-5">
									<i class="fa-regular fa-robot empty-icon text-primary"></i>
									<h3 class="h5 font-weight-bold mb-2">¡Hola! Soy Lexi</h3>
									<p class="text-muted mb-0">
										Cuéntame sobre tu problema legal y te ayudo a encontrar la mejor solución.
									</p>
								</div>

								<div
									v-for="message in messages"
									:key="message.id"
									class="chat-message d-flex"
								>
									<div class="chat-avatar">
										<i
											:class="message.role === 'user'
												? 'fa-regular fa-user'
												: 'fa-regular fa-robot'"
										></i>
									</div>
									<div class="chat-bubble-wrap flex-grow-1">
										<span class="chat-author">{{ message.role === 'user' ? 'Tú' : 'Lexi' }}</span>
										<div
											class="chat-bubble"
											:class="message.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-bot'"
										>
											<p class="mb-0">{{ message.content }}</p>
										</div>
									</div>
								</div>
							</div>

							<div class="chat-footer">
								<b-form class="d-flex" @submit.prevent="handleSubmit">
									<b-form-input
										v-model="inputValue"
										placeholder="Describe tu problema legal..."
										:disabled="isTyping"
										class="chat-input flex-grow-1 mr-2"
									></b-form-input>
									<b-button
										type="submit"
										variant="info"
										:disabled="!inputValue.trim() || isTyping"
									>
										<i class="fa-regular fa-paper-plane"></i>
									</b-button>
								</b-form>
							</div>
						</div>
					</div>
				</div>

				<!-- Paso 2: Expediente -->
				<div v-else-if="activeStep === 2" class="row">
					<div class="col-lg-3 mb-4 mb-lg-0">
						<div class="info-card">
							<h3 class="info-card-title">
								<i class="fa-regular fa-book-open mr-2 text-success"></i>
								Tu Expediente
							</h3>
							<p class="info-card-lead">
								Completa tu expediente digital para que los abogados puedan ayudarte mejor.
							</p>
							<ul class="info-list info-list-green list-unstyled mb-0">
								<li><span class="dot"></span> Información personal o empresarial</li>
								<li><span class="dot"></span> Datos de la contraparte</li>
								<li><span class="dot"></span> Documentos legales necesarios</li>
							</ul>
							<div class="info-card-note">
								<p class="mb-0 small text-muted">
									<strong>Importante:</strong> Toda tu información está protegida y será compartida únicamente con los abogados que elijas.
								</p>
							</div>
						</div>
					</div>

					<div class="col-lg-9">
						<div class="form-card">
							<div class="form-card-header">
								<h3 class="form-card-title mb-1">
									<i class="fa-regular fa-book-open mr-2 text-success"></i>
									Crea tu Expediente Digital
								</h3>
								<p class="text-muted small mb-0">
									Completa la información de tu caso para que los abogados puedan ayudarte mejor
								</p>
							</div>

							<div class="form-card-body">
								<!-- Tu Información -->
								<div class="accordion-item">
									<button
										type="button"
										class="accordion-toggle"
										:aria-expanded="openAccordion === 'tu-informacion'"
										@click="toggleAccordion('tu-informacion')"
									>
										<span>Tu Información</span>
										<i
											:class="openAccordion === 'tu-informacion'
												? 'fa-regular fa-chevron-up'
												: 'fa-regular fa-chevron-down'"
										></i>
									</button>
									<b-collapse :visible="openAccordion === 'tu-informacion'">
										<div class="accordion-body">
											<b-form-group label="Tipo de Entidad *">
												<div class="row">
													<div class="col-6">
														<button
															type="button"
															class="entity-btn w-100"
															:class="{ active: entityType === 'fisica' }"
															@click="entityType = 'fisica'"
														>
															Persona Física
														</button>
													</div>
													<div class="col-6">
														<button
															type="button"
															class="entity-btn w-100"
															:class="{ active: entityType === 'moral' }"
															@click="entityType = 'moral'"
														>
															Persona Moral
														</button>
													</div>
												</div>
											</b-form-group>

											<b-form-group
												v-if="entityType === 'moral'"
												label="Razón Social *"
												label-for="business-name"
											>
												<b-form-input
													id="business-name"
													v-model="businessName"
													placeholder="Ingresa la razón social de tu empresa"
												></b-form-input>
											</b-form-group>

											<b-form-group
												v-if="entityType === 'fisica'"
												label="Nombre Completo *"
												label-for="full-name"
											>
												<b-form-input
													id="full-name"
													v-model="fullName"
													placeholder="Ingresa tu nombre completo"
												></b-form-input>
											</b-form-group>

											<b-form-group
												v-if="entityType === 'moral'"
												label="Nombre de Representante Legal *"
												label-for="legal-rep-name"
											>
												<b-form-input
													id="legal-rep-name"
													v-model="legalRepName"
													placeholder="Ingresa el nombre del representante legal"
												></b-form-input>
											</b-form-group>

											<b-form-group label="RFC *" label-for="tax-id">
												<b-form-input
													id="tax-id"
													v-model="taxId"
													placeholder="Ingresa tu RFC"
												></b-form-input>
											</b-form-group>

											<template v-if="entityType === 'fisica'">
												<div class="file-field mb-3">
													<label class="d-block">INE *</label>
													<label class="file-picker" for="ine-fisica">
														<span>{{ ineFisica ? ineFisica.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="ine-fisica"
														type="file"
														accept=".pdf,.jpg,.jpeg,.png"
														class="d-none"
														@change="onSingleFile($event, 'ineFisica')"
													/>
													<small class="text-muted">Formatos aceptados: PDF, JPG, PNG</small>
												</div>

												<div class="file-field mb-3">
													<label class="d-block">Cédula Fiscal *</label>
													<label class="file-picker" for="cedula-fiscal-fisica">
														<span>{{ cedulaFiscalFisica ? cedulaFiscalFisica.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="cedula-fiscal-fisica"
														type="file"
														accept=".pdf,.jpg,.jpeg,.png"
														class="d-none"
														@change="onSingleFile($event, 'cedulaFiscalFisica')"
													/>
													<small class="text-muted">Formatos aceptados: PDF, JPG, PNG</small>
												</div>
											</template>

											<template v-if="entityType === 'moral'">
												<div class="file-field mb-3">
													<label class="d-block">Acta Constitutiva *</label>
													<label class="file-picker" for="acta-constitutiva">
														<span>{{ actaConstitutiva ? actaConstitutiva.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="acta-constitutiva"
														type="file"
														accept=".pdf"
														class="d-none"
														@change="onSingleFile($event, 'actaConstitutiva')"
													/>
													<small class="text-muted">Formato aceptado: PDF</small>
												</div>

												<div class="file-field mb-3">
													<label class="d-block">INE de Representante Legal *</label>
													<label class="file-picker" for="ine-representante">
														<span>{{ ineRepresentante ? ineRepresentante.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="ine-representante"
														type="file"
														accept=".pdf,.jpg,.jpeg,.png"
														class="d-none"
														@change="onSingleFile($event, 'ineRepresentante')"
													/>
													<small class="text-muted">Formatos aceptados: PDF, JPG, PNG</small>
												</div>

												<div class="file-field mb-3">
													<label class="d-block">Cédula Fiscal *</label>
													<label class="file-picker" for="cedula-fiscal-moral">
														<span>{{ cedulaFiscalMoral ? cedulaFiscalMoral.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="cedula-fiscal-moral"
														type="file"
														accept=".pdf,.jpg,.jpeg,.png"
														class="d-none"
														@change="onSingleFile($event, 'cedulaFiscalMoral')"
													/>
													<small class="text-muted">Formatos aceptados: PDF, JPG, PNG</small>
												</div>
											</template>

											<b-form-group label="Comentarios" label-for="comments">
												<b-form-textarea
													id="comments"
													v-model="comments"
													placeholder="Agrega cualquier información adicional relevante..."
													rows="4"
												></b-form-textarea>
											</b-form-group>
										</div>
									</b-collapse>
								</div>

								<!-- Contraparte -->
								<div class="accordion-item">
									<button
										type="button"
										class="accordion-toggle"
										:aria-expanded="openAccordion === 'contraparte'"
										@click="toggleAccordion('contraparte')"
									>
										<span>Información de la Contraparte</span>
										<i
											:class="openAccordion === 'contraparte'
												? 'fa-regular fa-chevron-up'
												: 'fa-regular fa-chevron-down'"
										></i>
									</button>
									<b-collapse :visible="openAccordion === 'contraparte'">
										<div class="accordion-body">
											<b-form-group label="Tipo de Entidad *">
												<div class="row">
													<div class="col-6">
														<button
															type="button"
															class="entity-btn w-100"
															:class="{ active: contraparte.entityType === 'fisica' }"
															@click="contraparte.entityType = 'fisica'"
														>
															Persona Física
														</button>
													</div>
													<div class="col-6">
														<button
															type="button"
															class="entity-btn w-100"
															:class="{ active: contraparte.entityType === 'moral' }"
															@click="contraparte.entityType = 'moral'"
														>
															Persona Moral
														</button>
													</div>
												</div>
											</b-form-group>

											<b-form-group
												v-if="contraparte.entityType === 'moral'"
												label="Razón Social *"
												label-for="contraparte-business-name"
											>
												<b-form-input
													id="contraparte-business-name"
													v-model="contraparte.businessName"
													placeholder="Ingresa la razón social de la contraparte"
												></b-form-input>
											</b-form-group>

											<b-form-group
												v-if="contraparte.entityType === 'fisica'"
												label="Nombre Completo *"
												label-for="contraparte-full-name"
											>
												<b-form-input
													id="contraparte-full-name"
													v-model="contraparte.fullName"
													placeholder="Ingresa el nombre completo de la contraparte"
												></b-form-input>
											</b-form-group>

											<b-form-group
												v-if="contraparte.entityType === 'moral'"
												label="Nombre de Representante Legal *"
												label-for="contraparte-legal-rep-name"
											>
												<b-form-input
													id="contraparte-legal-rep-name"
													v-model="contraparte.legalRepName"
													placeholder="Ingresa el nombre del representante legal"
												></b-form-input>
											</b-form-group>

											<b-form-group label="RFC *" label-for="contraparte-tax-id">
												<b-form-input
													id="contraparte-tax-id"
													v-model="contraparte.taxId"
													placeholder="Ingresa el RFC de la contraparte"
												></b-form-input>
											</b-form-group>

											<template v-if="contraparte.entityType === 'fisica'">
												<div class="file-field mb-3">
													<label class="d-block">INE *</label>
													<label class="file-picker" for="contraparte-ine-fisica">
														<span>{{ contraparte.ineFisica ? contraparte.ineFisica.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="contraparte-ine-fisica"
														type="file"
														accept=".pdf,.jpg,.jpeg,.png"
														class="d-none"
														@change="onContraparteFile($event, 'ineFisica')"
													/>
													<small class="text-muted">Formatos aceptados: PDF, JPG, PNG</small>
												</div>

												<div class="file-field mb-3">
													<label class="d-block">Cédula Fiscal *</label>
													<label class="file-picker" for="contraparte-cedula-fiscal-fisica">
														<span>{{ contraparte.cedulaFiscalFisica ? contraparte.cedulaFiscalFisica.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="contraparte-cedula-fiscal-fisica"
														type="file"
														accept=".pdf,.jpg,.jpeg,.png"
														class="d-none"
														@change="onContraparteFile($event, 'cedulaFiscalFisica')"
													/>
													<small class="text-muted">Formatos aceptados: PDF, JPG, PNG</small>
												</div>
											</template>

											<template v-if="contraparte.entityType === 'moral'">
												<div class="file-field mb-3">
													<label class="d-block">Acta Constitutiva *</label>
													<label class="file-picker" for="contraparte-acta-constitutiva">
														<span>{{ contraparte.actaConstitutiva ? contraparte.actaConstitutiva.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="contraparte-acta-constitutiva"
														type="file"
														accept=".pdf"
														class="d-none"
														@change="onContraparteFile($event, 'actaConstitutiva')"
													/>
													<small class="text-muted">Formato aceptado: PDF</small>
												</div>

												<div class="file-field mb-3">
													<label class="d-block">INE de Representante Legal *</label>
													<label class="file-picker" for="contraparte-ine-representante">
														<span>{{ contraparte.ineRepresentante ? contraparte.ineRepresentante.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="contraparte-ine-representante"
														type="file"
														accept=".pdf,.jpg,.jpeg,.png"
														class="d-none"
														@change="onContraparteFile($event, 'ineRepresentante')"
													/>
													<small class="text-muted">Formatos aceptados: PDF, JPG, PNG</small>
												</div>

												<div class="file-field mb-3">
													<label class="d-block">Cédula Fiscal *</label>
													<label class="file-picker" for="contraparte-cedula-fiscal-moral">
														<span>{{ contraparte.cedulaFiscalMoral ? contraparte.cedulaFiscalMoral.name : 'Seleccionar archivo...' }}</span>
														<i class="fa-regular fa-upload text-primary"></i>
													</label>
													<input
														id="contraparte-cedula-fiscal-moral"
														type="file"
														accept=".pdf,.jpg,.jpeg,.png"
														class="d-none"
														@change="onContraparteFile($event, 'cedulaFiscalMoral')"
													/>
													<small class="text-muted">Formatos aceptados: PDF, JPG, PNG</small>
												</div>
											</template>

											<b-form-group label="Comentarios" label-for="contraparte-comments">
												<b-form-textarea
													id="contraparte-comments"
													v-model="contraparte.comments"
													placeholder="Agrega cualquier información adicional relevante sobre la contraparte..."
													rows="4"
												></b-form-textarea>
											</b-form-group>
										</div>
									</b-collapse>
								</div>

								<!-- Otros archivos -->
								<div class="accordion-item">
									<button
										type="button"
										class="accordion-toggle"
										:aria-expanded="openAccordion === 'otros-archivos'"
										@click="toggleAccordion('otros-archivos')"
									>
										<span>Otros Archivos</span>
										<i
											:class="openAccordion === 'otros-archivos'
												? 'fa-regular fa-chevron-up'
												: 'fa-regular fa-chevron-down'"
										></i>
									</button>
									<b-collapse :visible="openAccordion === 'otros-archivos'">
										<div class="accordion-body">
											<p class="text-muted small mb-3">
												Sube cualquier documento adicional que consideres relevante para tu caso (contratos, correos, evidencias, etc.)
											</p>

											<div
												class="dropzone"
												:class="{ dragging: isDragging }"
												@dragover.prevent="isDragging = true"
												@dragleave.prevent="isDragging = false"
												@drop.prevent="handleDrop"
											>
												<input
													id="other-files"
													type="file"
													multiple
													accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
													class="d-none"
													@change="handleFileSelect"
												/>
												<label for="other-files" class="dropzone-label mb-0">
													<i class="fa-regular fa-upload dropzone-icon text-primary"></i>
													<p class="mb-1 text-muted">Arrastra archivos aquí o haz clic para seleccionar</p>
													<p class="mb-0 small text-muted">Formatos aceptados: PDF, JPG, PNG, DOC, DOCX</p>
												</label>
											</div>

											<div v-if="otherFiles.length" class="mt-3">
												<label class="d-block mb-2">Archivos cargados ({{ otherFiles.length }})</label>
												<div
													v-for="(file, index) in otherFiles"
													:key="index"
													class="file-list-item d-flex align-items-center justify-content-between"
												>
													<div class="d-flex align-items-center">
														<i class="fa-regular fa-upload text-primary mr-3"></i>
														<div>
															<p class="mb-0 font-weight-medium file-name">{{ file.name }}</p>
															<small class="text-muted">{{ formatFileSize(file.size) }}</small>
														</div>
													</div>
													<b-button
														variant="link"
														size="sm"
														class="text-dark"
														@click="removeFile(index)"
													>
														Eliminar
													</b-button>
												</div>
											</div>
										</div>
									</b-collapse>
								</div>
							</div>

							<div class="form-card-footer d-flex justify-content-between">
								<b-button variant="outline-secondary" @click="activeStep = 1">
									Volver al Chat
								</b-button>
								<b-button variant="success" @click="activeStep = 3">
									Guardar y Continuar
								</b-button>
							</div>
						</div>
					</div>
				</div>

				<!-- Paso 3: Abogados -->
				<div v-else class="lawyers-wrap mx-auto">
					<div class="form-card">
						<div class="form-card-header">
							<h3 class="form-card-title mb-1">Conecta con Abogados</h3>
							<p class="text-muted mb-0">Encuentra el especialista ideal para tu caso</p>
						</div>

						<div class="form-card-body">
							<p class="text-muted small mb-4">
								Basándonos en tu caso, estos son los profesionales que mejor pueden ayudarte:
							</p>

							<div class="row">
								<div
									v-for="lawyer in lawyers"
									:key="lawyer.id"
									class="col-md-4 mb-4"
								>
									<router-link
										:to="'/directorio/' + lawyer.id"
										class="lawyer-card d-block h-100 text-decoration-none"
									>
										<h4 class="lawyer-name">{{ lawyer.name }}</h4>
										<div class="lawyer-avatar mx-auto">
											<span>{{ lawyer.initials }}</span>
										</div>
										<span class="lawyer-badge">{{ lawyer.specialty }}</span>
										<div class="lawyer-phone">
											<i class="fa-regular fa-phone mr-1"></i>
											{{ lawyer.phone }}
										</div>
										<p class="lawyer-email mb-3">{{ lawyer.email }}</p>
										<b-button variant="info" block class="pointer-events-none">
											<i class="fa-regular fa-comments mr-2"></i>
											Contactar
										</b-button>
									</router-link>
								</div>
							</div>
						</div>

						<div class="form-card-footer d-flex justify-content-between align-items-center">
							<b-button variant="outline-secondary" @click="activeStep = 2">
								Volver al Expediente
							</b-button>
							<b-button to="/directorio" variant="info">
								<i class="fa-regular fa-book-open mr-2"></i>
								Ver más abogados
							</b-button>
						</div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
export default {
	data() {
		return {
			activeStep: 1,
			inputValue: '',
			messages: [],
			isTyping: false,
			openAccordion: 'tu-informacion',

			entityType: '',
			businessName: '',
			fullName: '',
			legalRepName: '',
			taxId: '',
			comments: '',
			ineFisica: null,
			cedulaFiscalFisica: null,
			actaConstitutiva: null,
			ineRepresentante: null,
			cedulaFiscalMoral: null,

			contraparte: {
				entityType: '',
				businessName: '',
				fullName: '',
				legalRepName: '',
				taxId: '',
				comments: '',
				ineFisica: null,
				cedulaFiscalFisica: null,
				actaConstitutiva: null,
				ineRepresentante: null,
				cedulaFiscalMoral: null,
			},

			otherFiles: [],
			isDragging: false,

			simulatedResponses: [
				'Entiendo tu situación. Para poder ayudarte mejor, necesito que me proporciones más detalles sobre tu caso. ¿Podrías contarme cuándo comenzó este problema y qué acciones has tomado hasta ahora?',
				'Basándome en lo que me cuentas, este tipo de casos generalmente requiere la intervención de un abogado especializado. Te recomiendo que documentes toda la información relevante: fechas, nombres, comunicaciones y cualquier evidencia que tengas.',
				'Este es un tema que requiere atención legal profesional. En unlawk contamos con abogados especializados en esta área que pueden ayudarte. ¿Te gustaría que te conecte con alguno de ellos?',
				'Para tu caso específico, te sugiero crear un expediente digital donde puedas organizar toda la documentación. Esto facilitará mucho el trabajo del abogado que elijas. ¿Quieres que te guíe en ese proceso?',
				'Perfecto. Te recomiendo seguir estos pasos: 1) Reúne toda la documentación relacionada, 2) Crea tu expediente digital en unlawk, 3) Consulta con uno de nuestros abogados especializados. ¿Necesitas ayuda con alguno de estos pasos?',
			],

			lawyers: [
				{
					id: 1,
					name: 'María Elena González Ruiz',
					initials: 'MG',
					specialty: 'DERECHO CIVIL',
					phone: '+52 55 1234 5678',
					email: 'maria.gonzalez@email.com',
				},
				{
					id: 2,
					name: 'Martínez & Asociados',
					initials: 'MA',
					specialty: 'DERECHO LABORAL',
					phone: '+52 55 1234 5678',
					email: 'contacto@martinezasociados.com',
				},
				{
					id: 3,
					name: 'Carlos Alberto Mendoza López',
					initials: 'CM',
					specialty: 'DERECHO LABORAL',
					phone: '+52 55 9876 5432',
					email: 'carlos.mendoza@email.com',
				},
			],
		};
	},

	methods: {
		handleSubmit() {
			if (!this.inputValue.trim() || this.isTyping) return;

			const userMessage = {
				id: String(Date.now()),
				role: 'user',
				content: this.inputValue,
			};
			const responseIndex = Math.min(this.messages.length, this.simulatedResponses.length - 1);

			this.messages.push(userMessage);
			this.inputValue = '';
			this.isTyping = true;
			this.$nextTick(this.scrollChatToBottom);

			setTimeout(() => {
				this.messages.push({
					id: String(Date.now() + 1),
					role: 'assistant',
					content: this.simulatedResponses[responseIndex],
				});
				this.isTyping = false;
				this.$nextTick(this.scrollChatToBottom);
			}, 1500);
		},

		scrollChatToBottom() {
			const el = this.$refs.chatScroll;
			if (el) el.scrollTop = el.scrollHeight;
		},

		toggleAccordion(id) {
			this.openAccordion = this.openAccordion === id ? null : id;
		},

		onSingleFile(event, field) {
			const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
			this[field] = file;
		},

		onContraparteFile(event, field) {
			const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
			this.$set(this.contraparte, field, file);
		},

		handleDrop(event) {
			this.isDragging = false;
			const files = Array.from(event.dataTransfer.files || []);
			this.otherFiles = this.otherFiles.concat(files);
		},

		handleFileSelect(event) {
			if (!event.target.files) return;
			const files = Array.from(event.target.files);
			this.otherFiles = this.otherFiles.concat(files);
			event.target.value = '';
		},

		removeFile(index) {
			this.otherFiles.splice(index, 1);
		},

		formatFileSize(size) {
			return (size / 1024).toFixed(2) + ' KB';
		},
	},
};
</script>
